import type { ChatMessage } from '../components/Chat';
import type { FilterPhase } from '../types/filterSidebar';

/**
 * Chat history lives in localStorage — the gateway has no thread listing
 * endpoint yet, so a saved chat is this device's copy of the conversation.
 */
const STORAGE_KEY = 'adiona_chats_v1';
const MAX_CHATS = 30;
const MAX_MESSAGES_PER_CHAT = 80;
const MAX_TITLE_LENGTH = 42;

export type StoredChat = {
    id: string;
    /** Server-side planner thread id; legacy local chats use `id` for both. */
    threadId?: string;
    title: string;
    /** Set once, on the chat's first save, and never touched again — this
     * is what the sidebar sorts by, so opening or adding to a chat doesn't
     * reshuffle the list. */
    createdAt: number;
    updatedAt: number;
    messages: ChatMessage[];
};

export const newChatId = () => `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** `other` messages carry React nodes, which cannot survive a round trip. */
const isStorable = (message: ChatMessage) => message.type !== 'other';

export const storableMessages = (messages: ChatMessage[]): ChatMessage[] =>
    messages.filter(isStorable).slice(-MAX_MESSAGES_PER_CHAT);

export const loadChats = (): StoredChat[] => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .filter(
                (chat: unknown): chat is StoredChat =>
                    Boolean(chat)
                    && typeof (chat as StoredChat).id === 'string'
                    && typeof (chat as StoredChat).title === 'string'
                    && Array.isArray((chat as StoredChat).messages)
            )
            // Chats saved before `createdAt` existed fall back to their
            // updatedAt so they still sort somewhere sane.
            .map((chat) => ({
                ...chat,
                threadId: chat.threadId || chat.id,
                createdAt: chat.createdAt ?? chat.updatedAt,
            }));
    } catch {
        // Corrupt payload or storage blocked (private mode) — start empty
        // rather than taking the whole page down.
        return [];
    }
};

export const saveChats = (chats: StoredChat[]) => {
    const ordered = [...chats].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_CHATS);
    // A long conversation full of widget envelopes can blow the quota; shed
    // the oldest chats until the write fits.
    for (let count = ordered.length; count > 0; count -= 1) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(ordered.slice(0, count)));
            return;
        } catch {
            // try again with fewer chats
        }
    }
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // storage unavailable — history is best-effort
    }
};

export const upsertChat = (chats: StoredChat[], chat: StoredChat): StoredChat[] => {
    // Keep the original createdAt if this chat already exists — the caller
    // always passes "now", since it doesn't know the chat's history.
    const existing = chats.find((c) => c.id === chat.id);
    const merged = existing ? { ...chat, createdAt: existing.createdAt } : chat;
    return [merged, ...chats.filter((c) => c.id !== chat.id)];
};

/** First thing the user said, trimmed to a sidebar-sized label. */
export const titleFromMessages = (messages: ChatMessage[]): string => {
    const first = messages.find((message) => message.type === 'text' && message.fromUser);
    const raw = (first && first.type === 'text' ? first.text : '').replace(/\s+/g, ' ').trim();
    if (!raw) return 'New trip';
    if (raw.length <= MAX_TITLE_LENGTH) return raw;

    const cut = raw.slice(0, MAX_TITLE_LENGTH);
    const lastSpace = cut.lastIndexOf(' ');
    const clipped = lastSpace > MAX_TITLE_LENGTH / 2 ? cut.slice(0, lastSpace) : cut;
    return `${clipped.replace(/[\s,.;:!?-]+$/, '')}…`;
};

/**
 * Reconstructs which SideBarRight filter sections a restored chat should
 * show, and which one should be open, by replaying its stored messages —
 * the same signals that drive them live (see Chat.tsx's onContentTypeChange
 * calls), just read back after the fact instead of as they happen.
 */
export const inferFilterPhases = (messages: ChatMessage[]): { seen: FilterPhase[]; open: FilterPhase } => {
    const seen: FilterPhase[] = [];
    let open: FilterPhase = 'idle';

    const mark = (phase: FilterPhase) => {
        if (!seen.includes(phase)) seen.push(phase);
        open = phase;
    };

    for (const message of messages) {
        if (message.type === 'hotels') mark('hotels');
        else if (message.type === 'rooms') mark('rooms');
        else if (message.type === 'flight') mark('flights');
        else if (message.type === 'widget') {
            const widgetType = String((message.envelope as Record<string, unknown>)?.widget_type || '');
            if (widgetType === 'flight_list' || widgetType === 'flight_card') mark('flights');
            else if (widgetType === 'hotel_list' || widgetType === 'hotel_card') mark('hotels');
            else if (widgetType === 'flight_search_form') mark('flights');
            else if (widgetType === 'hotel_search_form') mark('hotels');
            else if (widgetType === 'trip_search_form') { mark('flights'); mark('hotels'); }
            else if (widgetType === 'place_list' || widgetType === 'place_card') mark('destinations');
        }
    }

    return { seen, open };
};
